// src/pages/invoices/components/TaxSelect.tsx
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getTaxOptions, GstType, toTaxValue } from '@/lib/constants/invoice';
import { cn } from '@/lib/utils';
import { parseTaxValue } from '@/lib/utils/invoice';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

interface TaxSelectProps {
  value: string;
  onChange: (value: string) => void;
  isCustomerTaxable: boolean;
  taxType: GstType;
  error?: boolean;
}

const TaxSelect = ({ value, onChange, isCustomerTaxable, taxType, error }: TaxSelectProps) => {
  const [open, setOpen] = React.useState(false);

  const TAX_OPTIONS = React.useMemo(() => getTaxOptions(taxType), [taxType]);

  const visibleGroups = TAX_OPTIONS.filter((g) => g.taxable === isCustomerTaxable);

  // Older invoices stored only the rate ('18'); show them as the place-of-supply type
  const { gstType, rate } = parseTaxValue(value, taxType);
  const selectedValue = gstType ? toTaxValue(gstType, rate) : value;

  const allItems = TAX_OPTIONS.flatMap((g) => g.items);
  const selected = allItems.find((i) => i.value === selectedValue);

  const handleSelect = (optValue: string) => {
    onChange(optValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'hover:bg-accent hover:text-accent-foreground whitespace-nowrap',
          error && 'border-destructive ring-destructive/20'
        )}
      >
        {selected ? (
          <span className="truncate text-left flex-1">{selected.label}</span>
        ) : (
          <span className="text-muted-foreground">Select Tax</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>

      <PopoverContent className="w-[240px] p-0" align="start">
        <div className="max-h-72 overflow-auto p-1">
          {visibleGroups.map((group) => (
            <div key={group.group}>
              <div className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                <span>{group.group}</span>
                {group.recommended && <span className="font-medium text-primary">Recommended</span>}
              </div>
              {group.items.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={cn(
                    'flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                    selectedValue === opt.value && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedValue === opt.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{opt.label}</span>
                    {opt.description && (
                      <span className="text-xs text-muted-foreground">{opt.description}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TaxSelect;
